import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, ScrollRestoration, useLocation, useMatches } from 'react-router-dom';
import { Button } from '@/design-system/ui/Button';
import { Drawer } from '@/design-system/ui/Drawer';
import { cn } from '@/design-system/ui/cn';
import { FullPageSpinner } from '@/design-system/ui/States';
import { Icon, type IconName } from '@/design-system/ui/Icon';
import { ThemeToggle } from '@/design-system/ui/ThemeToggle';
import { Wordmark } from '@/design-system/ui/Wordmark';
import { useTheme } from '@/core/theme/theme';
import { useAuthStore } from '@/features/auth/model/auth-store';
import logo from '@/assets/images/app_logo.svg';
import { Footer } from './Footer';
import { ScrollTopButton } from './ScrollTopButton';

export interface RouteHandle {
  /** Booking/payment screens are focused flows: no bottom tab bar. */
  hideTabBar?: boolean;
}

const NAV: { to: string; label: string; icon: IconName }[] = [
  { to: '/', label: 'خانه', icon: 'home' },
  { to: '/tickets', label: 'بلیط‌ها', icon: 'ticket' },
  { to: '/track', label: 'رهگیری', icon: 'box' },
  { to: '/profile', label: 'حساب من', icon: 'user' },
];

/** Desktop header links. The first three stay at tablet width; the rest appear on large screens. */
const HEADER_LINKS: { to: string; label: string; wide?: boolean }[] = [
  { to: '/', label: 'خانه' },
  { to: '/tickets', label: 'بلیط‌ها' },
  { to: '/track', label: 'رهگیری مرسوله' },
  { to: '/magazine', label: 'مجله بلینو', wide: true },
  { to: '/why-blino', label: 'چرا بلینو؟', wide: true },
  { to: '/faq', label: 'سوالات متداول', wide: true },
];

const DRAWER_GROUPS: { title: string; links: { to: string; label: string; icon: IconName }[] }[] = [
  {
    title: 'خدمات',
    links: [
      { to: '/', label: 'بلیط بین‌شهری', icon: 'route' },
      { to: '/door-to-door', label: 'سفر درب‌به‌درب', icon: 'pin' },
      { to: '/track', label: 'رهگیری مرسوله', icon: 'box' },
    ],
  },
  {
    title: 'درباره بلینو',
    links: [
      { to: '/why-blino', label: 'چرا بلینو؟', icon: 'star' },
      { to: '/magazine', label: 'مجله بلینو', icon: 'article' },
      { to: '/faq', label: 'سوالات متداول', icon: 'help' },
    ],
  },
  {
    title: 'پشتیبانی',
    links: [
      { to: '/contact', label: 'تماس با پشتیبانی', icon: 'phone' },
      { to: '/complaint', label: 'ثبت شکایت', icon: 'alert' },
      { to: '/careers', label: 'همکاری با ما', icon: 'briefcase' },
    ],
  },
];

const isActivePath = (pathname: string, to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

/** Thin gradient bar under the header that fills as the page is read. */
function ScrollProgress() {
  const bar = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      bar.current?.style.setProperty('transform', `scaleX(${max > 0 ? Math.min(1, window.scrollY / max) : 0})`);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);
  return <span ref={bar} aria-hidden className="pointer-events-none absolute inset-x-0 bottom-[-1px] h-0.5 origin-right scale-x-0 bg-gradient-to-l from-mint via-turquoise to-blue" />;
}

function useScrolled(threshold = 12) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

/** Links sit in one rounded track; a white thumb slides to the current page. */
function DesktopNav() {
  const { pathname } = useLocation();
  const nav = useRef<HTMLElement>(null);
  const [thumb, setThumb] = useState<{ x: number; w: number } | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const el = nav.current?.querySelector<HTMLElement>('[aria-current="page"]');
      setThumb(el && el.offsetWidth > 0 ? { x: el.offsetLeft, w: el.offsetWidth } : null);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (nav.current) ro.observe(nav.current);
    return () => ro.disconnect();
  }, [pathname]);

  return (
    <nav ref={nav} aria-label="منوی اصلی" className="relative hidden items-center gap-0.5 rounded-full border border-line bg-fill/80 p-1 md:flex">
      <span
        aria-hidden
        className="absolute bottom-1 left-0 top-1 rounded-full bg-paper shadow-card ring-1 ring-line transition-[transform,width,opacity] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
        style={{ width: thumb?.w ?? 0, transform: `translateX(${thumb?.x ?? 0}px)`, opacity: thumb ? 1 : 0 }}
      />
      {HEADER_LINKS.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === '/'}
          className={({ isActive }) =>
            cn('relative z-10 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-bold transition-colors', isActive ? 'text-navy' : 'text-ink-2 hover:text-navy', n.wide && 'hidden lg:block')
          }
        >
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function ThemeRow() {
  const [theme, setTheme] = useTheme();
  const dark = theme === 'dark';
  return (
    <button type="button" role="switch" aria-checked={dark} onClick={() => setTheme(dark ? 'light' : 'dark')} className="flex w-full items-center gap-3 rounded-md bg-fill px-4 py-3 text-sm font-bold transition hover:bg-primary-light active:scale-[0.99]">
      <Icon name={dark ? 'moon' : 'sun'} size={20} className="text-navy" />
      <span className="flex-1 text-start">حالت تاریک</span>
      <span className={cn('relative h-6 w-11 rounded-full transition-colors', dark ? 'bg-turquoise' : 'bg-line')} aria-hidden>
        <span className={cn('absolute top-0.5 size-5 rounded-full bg-white shadow transition-all duration-200', dark ? 'start-[22px]' : 'start-0.5')} />
      </span>
    </button>
  );
}

function MenuDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { pathname } = useLocation();
  return (
    <Drawer open={open} onClose={onClose} title="منوی بلینو">
      <div className="flex items-center justify-between border-b border-line p-4">
        <span className="flex items-center gap-2 font-bold text-navy">
          <img src={logo} alt="" className="size-9 rounded-[10px]" />
          منوی بلینو
        </span>
        <button type="button" onClick={onClose} aria-label="بستن منو" className="grid size-9 place-items-center rounded-full text-ink-2 transition hover:bg-fill">
          <Icon name="close" size={20} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {DRAWER_GROUPS.map((g) => (
          <nav key={g.title} aria-label={g.title} className="py-2">
            <h2 className="px-5 pb-1 text-xs font-bold text-ink-3">{g.title}</h2>
            <ul>
              {g.links.map((l, i) => {
                const active = isActivePath(pathname, l.to) && !(l.to === '/' && pathname !== '/');
                return (
                  <li key={l.to + l.label} className="animate-rise" style={{ animationDelay: `${i * 45}ms` }}>
                    <Link
                      to={l.to}
                      onClick={onClose}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-3 border-s-[3px] px-5 py-3 text-[15px] font-bold transition-colors',
                        active ? 'border-turquoise bg-secondary-light/60 text-navy' : 'border-transparent text-ink hover:bg-fill',
                      )}
                    >
                      <Icon name={l.icon} size={20} className={active ? 'text-action' : 'text-ink-3'} />
                      <span className="flex-1">{l.label}</span>
                      <Icon name="chevronLeft" size={16} className="text-ink-3" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        ))}
      </div>
      <div className="flex flex-col gap-3 border-t border-line p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <ThemeRow />
        <div className="flex items-center gap-2 text-xs font-bold text-navy">
          <Icon name="verified" size={18} className="text-action" />
          رهگیری معتبر و رسمی
        </div>
      </div>
    </Drawer>
  );
}

function ScrollToTop() {
  const { pathname, search, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname, search, hash]);

  return null;
}

export function AppLayout() {
  const matches = useMatches();
  const { pathname } = useLocation();
  const hideTabBar = matches.some((m) => (m.handle as RouteHandle | undefined)?.hideTabBar);
  const loggedIn = useAuthStore((s) => Boolean(s.accessToken));
  const openLogin = useAuthStore((s) => s.openLogin);
  const scrolled = useScrolled();
  const [menuOpen, setMenuOpen] = useState(false);
  const [prevPath, setPrevPath] = useState(pathname);

  // Close the menu whenever the page changes (also covers the browser back button).
  if (prevPath !== pathname) {
    setPrevPath(pathname);
    setMenuOpen(false);
  }

  return (
    <div className="min-h-dvh bg-page">
      <ScrollRestoration />
      <ScrollToTop />
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:start-3 focus:top-3 focus:z-[200] focus:rounded-md focus:bg-solid focus:px-4 focus:py-2 focus:text-white">
        پرش به محتوا
      </a>
      <header
        className={cn(
          'sticky top-0 z-40 border-b bg-paper/90 pt-[env(safe-area-inset-top)] backdrop-blur-md transition-[box-shadow,border-color] duration-300 md:bg-paper/80 md:backdrop-blur-xl',
          scrolled ? 'border-line shadow-[0_8px_30px_-12px_rgb(10_34_57/0.28)]' : 'border-transparent',
        )}
      >
        <div className={cn('mx-auto grid max-w-none grid-cols-[auto_1fr_auto] items-center gap-3 px-4 sm:px-8 lg:px-12 transition-[height] duration-300 md:grid-cols-[1fr_auto_1fr]', scrolled ? 'h-14' : 'h-16')}>
          <Link to="/" className="group flex w-fit items-center gap-2.5" aria-label="بلینو، صفحه اصلی">
            <img src={logo} alt="" className={cn('rounded-[10px] transition-[width,height,transform] duration-300 group-hover:-rotate-3 group-hover:scale-105', scrolled ? 'size-9' : 'size-10')} />
            <Wordmark className={cn('transition-[height] duration-300', scrolled ? 'h-7' : 'h-8')} />
          </Link>

          <div className="flex justify-center">
            <DesktopNav />
          </div>

          <div className="flex items-center justify-end gap-2">
            <ThemeToggle className="hidden md:grid" />
            {loggedIn ? (
              <Link to="/profile" aria-label="حساب کاربری" className="hidden size-10 place-items-center rounded-full bg-cta text-white shadow-glow transition hover:brightness-105 active:scale-90 md:grid">
                <Icon name="user" size={20} />
              </Link>
            ) : (
              <Button size="sm" className="rounded-full" onClick={() => openLogin()}>
                ورود<span className="hidden sm:inline"> / ثبت‌نام</span>
              </Button>
            )}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="باز کردن منو"
              aria-haspopup="dialog"
              className="grid size-10 place-items-center rounded-full border border-line bg-paper text-ink transition hover:border-turquoise/50 hover:bg-fill active:scale-90 lg:hidden"
            >
              <Icon name="menu" size={20} />
            </button>
          </div>
        </div>
        <ScrollProgress />
      </header>
      <MenuDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />

      <main id="main" className="min-h-[60dvh]">
        <Suspense fallback={<FullPageSpinner />}>
          {/* Re-keyed per page so every navigation fades and lifts the new screen in. */}
          <div key={pathname} className="animate-page-in">
            <ScrollToTop />
            <Outlet />
          </div>
        </Suspense>
      </main>

      {/* Booking/payment are focused flows: no footer there, like the tab bar. */}
      {!hideTabBar && <Footer className="mt-12 bg-navy-deep pb-[calc(7rem+env(safe-area-inset-bottom))] md:pb-0" />}

      <ScrollTopButton />

      {!hideTabBar && (
        <nav
          aria-label="ناوبری"
          data-tabbar
          className="fixed inset-x-3 bottom-[max(0.75rem,calc(env(safe-area-inset-bottom)-0.25rem))] z-40 mx-auto max-w-md rounded-[24px] border border-line bg-paper/95 p-1.5 shadow-floating backdrop-blur-md transition-[transform,opacity] duration-200 md:hidden"
        >
          <ul className="grid grid-cols-4">
            {NAV.map((n) => (
              <li key={n.to}>
                <NavLink
                  to={n.to}
                  end={n.to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-[3.5rem] flex-col items-center justify-center gap-0.5 rounded-[18px] px-1 text-[11px] font-bold leading-tight transition active:scale-95',
                      isActive ? 'bg-primary-light text-navy shadow-[inset_0_0_0_1px_rgb(0_168_150/0.18)]' : 'text-ink-3 hover:text-ink-2',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon name={n.icon} size={22} className={cn('transition-transform duration-300', isActive && 'animate-seat-pop -translate-y-0.5 scale-110 text-action')} />
                      <span className="truncate">{n.label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
